import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';

const milestones = [
  { year: '2018', title: '平台成立', desc: '海角社区正式上线，初心是打造一个有温度的情感交流平台。' },
  { year: '2019', title: '用户突破10万', desc: '凭借真实的内容和温暖的社区氛围，注册用户快速增长。' },
  { year: '2020', title: '内容生态完善', desc: '拓展六大内容板块，覆盖情感倾诉、生活故事等多个领域。' },
  { year: '2021', title: '用户突破50万', desc: '社区日均活跃用户持续增长，成为国内知名情感社区。' },
  { year: '2023', title: '百万用户里程碑', desc: '注册用户突破100万，累计发布故事超过200万篇。' },
  { year: '2026', title: '不断创新', desc: '持续优化产品体验，拓展更多内容形态，连接更多真实用户。' },
];

const values = [
  { icon: 'ri-heart-pulse-line', title: '真实', desc: '我们坚信真实的力量，每篇内容都来自用户的真实经历与情感表达。' },
  { icon: 'ri-shield-star-line', title: '温暖', desc: '营造包容友善的社区氛围，让每个人都能在这里找到理解与支持。' },
  { icon: 'ri-user-heart-line', title: '连接', desc: '通过共同的经历与话题，帮助用户找到志同道合的伙伴。' },
  { icon: 'ri-lightbulb-flash-line', title: '成长', desc: '鼓励自我表达与内在探索，陪伴用户在分享中共同成长。' },
];

const testimonials = [
  {
    name: '小雨',
    role: '注册用户 3年',
    content: '海角社区就像我的树洞，很多不能和身边人说的话，在这里找到了倾听的人。感谢每一个温暖的评论。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20young%20woman%20warm%20gentle%20smile%20portrait%20soft%20lighting%20cream%20background%20clean%20simple&width=120&height=120&seq=testimonial-01&orientation=squarish',
  },
  {
    name: '阿杰',
    role: '活跃作者',
    content: '在这里分享了两年的生活故事，收获了很多真诚的反馈。海角社区让我知道，我不是一个人在经历这些。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20young%20man%20friendly%20smile%20portrait%20natural%20light%20clean%20background%20simple%20style&width=120&height=120&seq=testimonial-02&orientation=squarish',
  },
  {
    name: '梅子',
    role: '社区版主',
    content: '从用户到版主，我见证了海角社区的成长。这里越来越像一个大家庭，每个人都在用心经营这份温暖。',
    avatar: 'https://readdy.ai/api/search-image?query=Chinese%20woman%20mature%20confident%20smile%20portrait%20professional%20warm%20lighting%20simple%20background&width=120&height=120&seq=testimonial-03&orientation=squarish',
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.haijiao.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.haijiao.cn/about' },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '海角社区',
    url: 'https://www.haijiao.cn/',
    description: '真实用户聚集的情感交流平台',
    email: 'contact@haijiao.cn',
    telephone: '400-888-6666',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '杭州',
      addressRegion: '浙江',
      addressCountry: 'CN',
      streetAddress: '文三路478号',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于海角社区',
    description: '海角社区是国内知名的情感交流与生活分享社区。了解我们的品牌故事、品牌理念、用户口碑与联系方式。',
    url: 'https://www.haijiao.cn/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '海角社区是什么？', acceptedAnswer: { '@type': 'Answer', text: '海角社区是国内知名的情感交流与生活分享社区平台，汇聚了来自全国各地的真实用户。' } },
      { '@type': 'Question', name: '海角社区是否免费使用？', acceptedAnswer: { '@type': 'Answer', text: '是的，海角社区完全免费，所有基础功能均不收取任何费用。' } },
      { '@type': 'Question', name: '我的隐私信息安全吗？', acceptedAnswer: { '@type': 'Answer', text: '海角社区高度重视用户隐私保护，采用业界先进的数据加密技术。' } },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
            </div>
          </div>

          {/* Hero */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20abstract%20community%20atmosphere%20soft%20gradient%20orange%20peach%20cream%20tones%20peaceful%20dreamy%20blur%20background%20emotional%20connection%20artistic%20style&width=1800&height=700&seq=about-hero-2026&orientation=landscape"
              alt="关于海角社区"
              title="海角社区品牌介绍"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                关于海角社区
              </h1>
              <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-xl mx-auto">
                始于2018年，致力于打造真实、温暖、有深度的情感交流平台。数百万用户在这里倾诉、分享、成长，让每一个故事都被温柔以待。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                品牌故事
              </h2>
              <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  海角社区创立于2018年，创始团队怀着"每一个故事都值得被倾听"的信念，搭建了这座连接人心的桥梁。我们深知，在这个快节奏的时代，每个人都需要一个可以卸下伪装、真诚交流的空间。
                </p>
                <p>
                  从最初的几百个用户，到如今数百万真实用户在这里聚集，海角社区始终坚持内容为本、用户至上的理念。我们不做浮夸的推荐算法，不追求流量至上的运营策略，而是致力于营造一个包容、友善、真实的社区氛围。
                </p>
                <p>
                  在这里，你可以倾诉情感的困惑，分享生活的点滴，记录成长的历程，结交志同道合的朋友。每一个故事都可能触动另一个灵魂，每一次交流都在让这个世界多一份理解与温暖。
                </p>
              </div>

              {/* Timeline */}
              <div className="mt-14 relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6">
                  {milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="flex-1 md:text-right">
                        <div className="bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5">
                          <span className="text-xs font-bold text-primary-600 bg-primary-100 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                            {m.year}
                          </span>
                          <h3 className="text-sm font-semibold text-foreground-900 mt-2 mb-1">
                            {m.title}
                          </h3>
                          <p className="text-xs text-foreground-600">{m.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center justify-center w-8 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500"></div>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
                品牌理念
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10 max-w-md mx-auto">
                四个核心价值，指引我们不断前行
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {values.map((v) => (
                  <div
                    key={v.title}
                    className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10 max-w-md mx-auto">
                来自真实用户的评价和感受
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.name}
                    className="rounded-xl bg-background-100 border border-background-200/70 p-5 md:p-6"
                  >
                    <div className="flex items-center gap-1 text-secondary-400 mb-3">
                      <i className="ri-star-fill text-sm"></i>
                      <i className="ri-star-fill text-sm"></i>
                      <i className="ri-star-fill text-sm"></i>
                      <i className="ri-star-fill text-sm"></i>
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10 max-w-md mx-auto">
                有任何问题或建议，欢迎随时与我们取得联系
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">contact@haijiao.cn</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-600">400-888-6666</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-600">杭州市西湖区文三路478号</p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}